import * as XLSX from 'xlsx';
import {
  LEAD_IMPORT_IGNORE,
  LEAD_IMPORT_NOT_MAPPED,
  type LeadImportColumnMapping,
  type LeadImportErrorRow,
  type LeadImportExecuteResult,
  type LeadImportFieldDefinition,
  type LeadImportFieldKey,
  type LeadImportParsedFile,
} from '../models/lead-import.model';

const SAMPLE_ROW_COUNT = 5;

function normalizeHeader(header: string): string {
  return header
    .trim()
    .toLowerCase()
    .replace(/[_\-./]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[^\w\s]/g, '')
    .trim();
}

function toCellText(value: unknown): string {
  if (value == null) return '';
  return String(value).trim();
}

export function autoMapImportColumn(
  header: string,
  definitions: LeadImportFieldDefinition[],
): LeadImportFieldKey | typeof LEAD_IMPORT_IGNORE | typeof LEAD_IMPORT_NOT_MAPPED {
  const normalized = normalizeHeader(header);
  if (!normalized) return LEAD_IMPORT_NOT_MAPPED;

  for (const field of definitions) {
    if (normalizeHeader(field.label) === normalized) return field.key;
    for (const synonym of field.synonyms) {
      if (normalizeHeader(synonym) === normalized) return field.key;
    }
  }

  return LEAD_IMPORT_NOT_MAPPED;
}

export function buildAutoColumnMapping(
  headers: string[],
  definitions: LeadImportFieldDefinition[],
): LeadImportColumnMapping {
  const mapping: LeadImportColumnMapping = {};
  const used = new Set<string>();

  for (const header of headers) {
    const match = autoMapImportColumn(header, definitions);
    if (match === LEAD_IMPORT_NOT_MAPPED || match === LEAD_IMPORT_IGNORE) {
      mapping[header] = match;
      continue;
    }
    if (used.has(match)) {
      mapping[header] = LEAD_IMPORT_NOT_MAPPED;
      continue;
    }
    used.add(match);
    mapping[header] = match;
  }

  return mapping;
}

export async function parseLeadImportFile(file: File): Promise<LeadImportParsedFile> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    return { headers: [], rows: [], sampleRows: [] };
  }

  const sheet = workbook.Sheets[sheetName];
  const table = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const rows = table.map((row) => {
    const mapped: Record<string, string> = {};
    for (const [header, value] of Object.entries(row)) {
      mapped[header] = toCellText(value);
    }
    return mapped;
  });

  const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
  return {
    headers,
    rows,
    sampleRows: rows.slice(0, SAMPLE_ROW_COUNT),
  };
}

export function collectUnknownStreamValues(
  rows: Record<string, string>[],
  columnMapping: LeadImportColumnMapping,
  streamValueMappings: Record<string, string>,
  allowedStreams: readonly string[],
): string[] {
  const streamHeader = Object.entries(columnMapping).find(([, key]) => key === 'stream')?.[0];
  if (!streamHeader) return [];

  const unknown = new Set<string>();
  const allowed = new Set(allowedStreams.map((value) => value.toLowerCase()));

  for (const row of rows) {
    const raw = row[streamHeader]?.trim();
    if (!raw) continue;
    if (streamValueMappings[raw]) continue;
    if (allowed.has(raw.toLowerCase())) continue;
    unknown.add(raw);
  }

  return [...unknown].sort((a, b) => a.localeCompare(b));
}

export function buildErrorReportExcel(
  errors: LeadImportErrorRow[],
  filename = 'lead-import-errors.xlsx',
): void {
  if (typeof document === 'undefined' || errors.length === 0) return;

  const originalHeaders = [...new Set(errors.flatMap((row) => Object.keys(row.originalRow)))];
  const header = ['Row Number', ...originalHeaders, 'Failure Reason'];
  const data = errors.map((row) => [
    row.rowIndex,
    ...originalHeaders.map((key) => row.originalRow[key] ?? ''),
    row.reason,
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([header, ...data]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Import Errors');
  XLSX.writeFile(workbook, filename);
}

export function buildErrorReportCsv(
  errors: LeadImportErrorRow[],
  filename = 'lead-import-errors.csv',
): void {
  if (typeof document === 'undefined' || errors.length === 0) return;

  const originalHeaders = [...new Set(errors.flatMap((row) => Object.keys(row.originalRow)))];
  const header = ['Row Number', ...originalHeaders, 'Failure Reason'];

  const escape = (value: unknown) => {
    const text = String(value ?? '');
    if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
    return text;
  };

  const lines = [
    header.map(escape).join(','),
    ...errors.map((row) =>
      [row.rowIndex, ...originalHeaders.map((key) => row.originalRow[key] ?? ''), row.reason]
        .map(escape)
        .join(','),
    ),
  ];

  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function countImportableRows(
  aggregates: { valid: number; warning: number; duplicate: number },
  includeWarnings: boolean,
): number {
  return aggregates.valid + aggregates.duplicate + (includeWarnings ? aggregates.warning : 0);
}

const EMPTY_IMPORT_RESULT: LeadImportExecuteResult = {
  total: 0,
  created: 0,
  updated: 0,
  skippedDuplicates: 0,
  skippedErrors: 0,
  skippedWarnings: 0,
  failed: 0,
  errors: [],
};

export function mergeImportExecuteResults(
  results: LeadImportExecuteResult[],
): LeadImportExecuteResult {
  return results.reduce(
    (acc, result) => ({
      total: acc.total + result.total,
      created: acc.created + result.created,
      updated: acc.updated + result.updated,
      skippedDuplicates: acc.skippedDuplicates + result.skippedDuplicates,
      skippedErrors: acc.skippedErrors + result.skippedErrors,
      skippedWarnings: acc.skippedWarnings + result.skippedWarnings,
      failed: acc.failed + result.failed,
      errors: [...acc.errors, ...result.errors],
    }),
    { ...EMPTY_IMPORT_RESULT, errors: [] as LeadImportExecuteResult['errors'] },
  );
}

export function adjustImportErrorRowIndices(
  errors: LeadImportExecuteResult['errors'],
  batchStartIndex: number,
): LeadImportExecuteResult['errors'] {
  return errors.map((error) => ({
    ...error,
    rowIndex: batchStartIndex + error.rowIndex,
  }));
}

export const LEAD_IMPORT_BATCH_SIZE = 10;
export const LEAD_IMPORT_PREVIEW_PAGE_SIZE = 10;
